import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Flag, Send, ThumbsUp, Trash2, Pencil } from 'lucide-react';
import { useVerification } from '../../hooks/useVerification';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';
import FormattedText from '../common/FormattedText';
import PostEditor from '../common/PostEditor';
import TagInput from '../common/TagInput';
import { parseTags } from '../../lib/tags';
import PostImages from '../common/PostImages';
import { syncContentImages, imageErrorMessage, sweepImageDeletions } from '../../lib/postImages';
import YouTubePreview from '../common/YouTubePreview';
import { firstYouTubeVideoId } from '../../lib/youtubeLinks';
import { VIDEO_SUGGESTION_MESSAGES } from '../../lib/videoLibrary';
import VerifyModal from '../common/VerifyModal';
import ReportModal from '../common/ReportModal';
import EditedMark from '../common/EditedMark';
import useToast from '../../hooks/useToast';
import Toast from '../common/Toast';
import { getRoleLabel } from '../../lib/roleLabels';
import TeamTag from '../common/TeamTag';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

const skeletonBg = 'var(--card-border)';

// ── Relative time helper ────────────────────────────────────

const relativeTime = (isoString) => {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days >= 7) return `${Math.floor(days / 7)}w ago`;
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
};

// ── Reply component (module scope) ──────────────────────────

const ReplyCard = ({ reply, navigate, isFirst, canReport, onReport, canEdit, onSave, onDelete, onToggleLike, userId }) => {
  const displayName = (reply.author_use_username && reply.author_username) ? reply.author_username : (reply.author_name || 'DSP');

  // Edit state is local to the card so opening one editor doesn't re-render or
  // reset the others, and so a cancelled edit leaves no trace in the parent.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reply.content);
  const [draftImages, setDraftImages] = useState(reply.images ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = () => {
    setDraft(reply.content);   // re-seed: the reply may have changed since mount
    setDraftImages(reply.images ?? []);
    setError('');
    setEditing(true);
  };

  const save = async () => {
    const next = draft.trim();
    if (!next || saving) return;
    const imagesChanged =
      JSON.stringify((draftImages ?? []).map((i) => i.path)) !==
      JSON.stringify((reply.images ?? []).map((i) => i.path));
    // No-op: don't mark it edited for a save that changes nothing.
    if (next === reply.content && !imagesChanged) { setEditing(false); return; }
    setSaving(true);
    const msg = await onSave(reply.id, next, draftImages, imagesChanged);
    setSaving(false);
    if (msg) { setError(msg); return; }
    setEditing(false);
  };

  const replyVideoId = firstYouTubeVideoId(reply.content);

  return (
    <div
      className="flex gap-3 py-4"
      style={isFirst ? undefined : { borderTop: '1px solid var(--card-border)' }}
    >
      {reply.author_avatar ? (
        <img src={reply.author_avatar} alt={displayName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 bg-gradient-to-br from-brand-primary to-brand-secondary">
          {displayName[0]}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <button
            onClick={() => navigate(`/profile/${reply.author_id}`)}
            className="text-xs font-semibold text-primary text-left hover:underline transition-colors"
          >
            {displayName}
          </button>
          <TeamTag userId={reply.author_id} />
          <span className="text-xs text-tertiary">{relativeTime(reply.created_at)}</span>
          <EditedMark at={reply.edited_at} className="text-xs" />
          <div className="ml-auto flex items-center gap-2">
            {canEdit && !editing && (
              <button
                onClick={startEdit}
                className="transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
                style={{ color: 'var(--text-tertiary)' }}
                title="Edit reply"
              >
                <Pencil size={13} />
              </button>
            )}
            {/* delete_reply also allows moderator+ (036); here, like posts, only the author. */}
            {canEdit && !editing && (
              <button
                onClick={() => onDelete(reply.id)}
                className="transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
                style={{ color: 'var(--text-tertiary)' }}
                title="Delete reply"
              >
                <Trash2 size={13} />
              </button>
            )}
            {canReport && (
              <button
                onClick={onReport}
                className="transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
                style={{ color: 'var(--text-tertiary)' }}
                title="Report reply"
              >
                <Flag size={13} />
              </button>
            )}
          </div>
        </div>

        {editing ? (
          <div className="mt-1">
            <PostEditor
              value={draft}
              onChange={setDraft}
              maxLength={2000}
              size="reply"
              ariaLabel="Edit reply"
              onSubmit={save}
              autoFocus
              images={draftImages}
              onImagesChange={setDraftImages}
              userId={userId}
            />
            {error && <p className="text-xs mt-1" style={{ color: 'var(--color-error)' }}>{error}</p>}
            <div className="flex items-center justify-end mt-2">
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-1.5 rounded-lg text-xs transition-all active:scale-[0.98]"
                  style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving || !draft.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <FormattedText text={reply.content} className="text-[15px] text-secondary leading-relaxed" />
            <PostImages images={reply.images} className="mt-2 max-w-md" />
            {replyVideoId && <YouTubePreview videoId={replyVideoId} className="mt-2 max-w-md" />}
            {/* canEdit === your own reply: a plain count, no vote (052). */}
            {canEdit ? (
              <span
                className="mt-2 flex items-center gap-1.5"
                style={{ color: 'var(--text-tertiary)' }}
                title="Helpful votes on your reply"
              >
                <ThumbsUp size={14} aria-hidden="true" />
                <span className="text-xs font-medium">{reply.helpful_count ?? 0}<span className="sr-only"> helpful votes</span></span>
              </span>
            ) : (
              <button
                onClick={() => onToggleLike(reply.id)}
                className="mt-2 flex items-center gap-1.5 transition-all duration-200 hover:scale-110 active:scale-95"
                style={{ color: reply.user_liked ? 'var(--brand-primary)' : 'var(--text-tertiary)' }}
                aria-pressed={!!reply.user_liked}
                aria-label={`Helpful (${reply.helpful_count ?? 0})`}
                title="Helpful"
              >
                <ThumbsUp size={14} className={reply.user_liked ? 'fill-current' : ''} />
                <span className="text-xs font-medium">{reply.helpful_count ?? 0}</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────

const PostDetailScreen = () => {
  const { tableName: tableSlug, postId } = useParams();
  const navigate = useNavigate();
  const { showVerifyModal, setShowVerifyModal, requireVerified } = useVerification();
  const { session } = useUser();
  const { toastMsg, showToast } = useToast();
  const location = useLocation();

  // Outcome of "Also suggest this video for the library", handed over by the
  // composer that just created this post. Shown once, then cleared from history
  // state so a reload or back-navigation doesn't repeat it.
  useEffect(() => {
    const outcome = location.state?.videoSuggestion;
    const imgError = location.state?.imageError;
    if (!imgError && (!outcome || !VIDEO_SUGGESTION_MESSAGES[outcome])) return;
    // The post went up either way; this says what didn't come with it.
    showToast(imgError || VIDEO_SUGGESTION_MESSAGES[outcome], 5000);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate, showToast]);
  const currentUserId = session?.user?.id ?? null;

  // ── Post state ──────────────────────────────────────────
  const [post, setPost] = useState(null);
  const [postLoading, setPostLoading] = useState(true);
  const [postError, setPostError] = useState(false);

  // ── Like state ──────────────────────────────────────────
  const [liked, setLiked] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(0);

  // ── Replies state ───────────────────────────────────────
  const [replies, setReplies] = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(true);

  // ── Reply compose ───────────────────────────────────────
  const [replyContent, setReplyContent] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  // ── Report modal ({ type, id } | null) ──────────────────
  const [reportTarget, setReportTarget] = useState(null);

  // ── Post edit ───────────────────────────────────────────
  const [editingPost, setEditingPost] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editImages, setEditImages] = useState([]);
  const [postImages, setPostImages] = useState([]);
  const [replyImages, setReplyImages] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // ── Fetch post ──────────────────────────────────────────
  const fetchPost = useCallback(async () => {
    // get_table_posts doesn't have a single-post variant, so we fetch
    // the post row directly + profile join via a targeted RPC call.
    // For now, use get_table_posts with the table looked up from the post itself.
    // Simpler: fetch the post row directly, then fetch author profile.
    // Known gap: this direct select bypasses the user_blocks filter that the
    // list RPCs apply, so a blocked author's post stays reachable by direct
    // link (replies below ARE filtered via get_post_replies).
    const { data: postRows, error } = await supabase
      .from('community_posts')
      .select('*')
      .eq('id', postId)
      .limit(1);

    if (error || !postRows || postRows.length === 0) {
      console.error('fetch post:', error);
      setPostError(true);
      setPostLoading(false);
      return;
    }

    const postRow = postRows[0];

    // Fetch author profile through the public-profile RPC, not `profiles`
    // (self-read RLS): it applies the privacy toggles, including 056's rule
    // that hides agency when switched off and a family member's location always.
    const { data: profileRows } = await supabase
      .rpc('get_public_profile', { p_user_id: postRow.author_id });

    const profile = profileRows?.[0];

    // Check if user liked this post
    const { data: likeRows } = await supabase
      .from('community_post_likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
      .limit(1);

    const postData = {
      ...postRow,
      author_name: (profile?.use_username && profile?.username) ? profile.username : (profile?.name || 'DSP'),
      author_role: profile?.role || '',
      author_agency: profile?.agency || '',
      author_avatar: profile?.avatar_url || null,
      user_liked: (likeRows?.length || 0) > 0,
    };

    // The post itself is read with a direct select, so its photos come from
    // their own RPC (041). Replies get theirs inside get_post_replies.
    const { data: imageJson } = await supabase.rpc('get_post_images', { p_post_id: postId });
    setPostImages(Array.isArray(imageJson) ? imageJson : []);

    setPost(postData);
    setLiked(postData.user_liked);
    setHelpfulCount(postData.helpful_count);
    setPostLoading(false);
  }, [postId]);

  // Is this post's table archived (040)? The reply box is hidden if so — a DB
  // trigger refuses the insert either way, but a compose box that always fails
  // is exactly the dead affordance the 2026-09-08 pass removed elsewhere.
  const [tableArchived, setTableArchived] = useState(false);

  useEffect(() => {
    if (!tableSlug) return;
    let cancelled = false;
    supabase.rpc('get_community_table', { p_slug: tableSlug }).then(({ data, error }) => {
      if (cancelled || error) return;
      setTableArchived(data?.[0]?.active === false);
    });
    return () => { cancelled = true; };
  }, [tableSlug]);

  // ── Fetch replies ───────────────────────────────────────
  const fetchReplies = useCallback(async () => {
    setRepliesLoading(true);
    const { data, error } = await supabase.rpc('get_post_replies', { p_post_id: postId });
    if (error) {
      console.error('get_post_replies:', error);
    }
    setReplies(data ?? []);
    setRepliesLoading(false);
  }, [postId]);

  // ── Mount: fetch post + replies ─────────────────────────
  useEffect(() => {
    fetchPost();
    fetchReplies();
  }, [fetchPost, fetchReplies]);

  // ── Toggle helpful ──────────────────────────────────────
  const handleToggleLike = useCallback(async () => {
    if (!requireVerified()) return;

    const prevLiked = liked;
    const prevCount = helpfulCount;

    // Optimistic
    setLiked(!prevLiked);
    setHelpfulCount(prevLiked ? Math.max(prevCount - 1, 0) : prevCount + 1);

    const { data: newCount, error } = await supabase.rpc('toggle_post_like', { p_post_id: postId });

    if (error) {
      setLiked(prevLiked);
      setHelpfulCount(prevCount);
      const msg = error.message?.includes('not_verified')
        ? 'Verification required to mark helpful.'
        : 'Could not mark helpful. Please try again.';
      showToast(msg);
    } else {
      setHelpfulCount(newCount);
    }
  }, [liked, helpfulCount, postId, requireVerified, showToast]);

  // ── Toggle helpful on a reply (051) ─────────────────────
  // Flipping is its own inverse, so an error rolls back by flipping again —
  // no snapshot of the replies array needed.
  const handleToggleReplyLike = useCallback(async (replyId) => {
    if (!requireVerified()) return;

    const flip = (r) => (r.id === replyId
      ? {
          ...r,
          user_liked: !r.user_liked,
          helpful_count: Math.max((r.helpful_count ?? 0) + (r.user_liked ? -1 : 1), 0),
        }
      : r);
    setReplies(prev => prev.map(flip));

    const { data: newCount, error } = await supabase.rpc('toggle_reply_like', { p_reply_id: replyId });

    if (error) {
      setReplies(prev => prev.map(flip));
      const msg = error.message?.includes('not_verified')
        ? 'Verification required to mark helpful.'
        : 'Could not mark helpful. Please try again.';
      showToast(msg);
    } else {
      setReplies(prev => prev.map(r => (r.id === replyId ? { ...r, helpful_count: newCount } : r)));
    }
  }, [requireVerified, showToast]);

  // ── Edit ────────────────────────────────────────────────
  // Shared error mapping: the update RPCs raise the same names as their create
  // counterparts, so one translation serves posts, replies and comments.
  const editErrorMessage = (error) => {
    const m = error?.message ?? '';
    if (m.includes('not_verified')) return 'Verification required to edit.';
    if (m.includes('unauthorized')) return 'You can only edit your own posts.';
    if (m.includes('content_required')) return 'Content can\'t be empty.';
    if (m.includes('subject_required')) return 'Subject can\'t be empty.';
    if (m.includes('_too_long')) return 'That\'s too long — please shorten it.';
    if (m.includes('_not_found')) return 'That content no longer exists.';
    return 'Could not save. Please try again.';
  };

  // Returns an error string on failure, null on success — the ReplyCard owns
  // its own editor state and needs to know whether to close it.
  const handleSaveReply = useCallback(async (replyId, content, images, imagesChanged) => {
    const { error } = await supabase.rpc('update_reply', {
      p_reply_id: replyId,
      p_content: content,
    });
    if (error) return editErrorMessage(error);
    if (imagesChanged) {
      const { error: imgErr } = await syncContentImages({ replyId, images });
      if (imgErr) return imageErrorMessage(imgErr.message);
      sweepImageDeletions().catch(() => {});   // removed photos' files
    }
    // Patch in place rather than refetching: a refetch would reorder nothing
    // but would flash the whole thread.
    setReplies(prev => prev.map(r => (
      r.id === replyId
        ? { ...r, content, images: images ?? r.images, edited_at: new Date().toISOString() }
        : r
    )));
    return null;
  }, []);

  const handleSavePost = useCallback(async () => {
    if (editSaving) return;   // ⌘/Ctrl+Enter bypasses the disabled button
    const subject = editSubject.trim();
    const content = editContent.trim();
    if (!subject || !content) {
      setEditError(!subject ? 'Subject can\'t be empty.' : 'Content can\'t be empty.');
      return;
    }
    const tags = parseTags(editTags);

    setEditSaving(true);
    const { error } = await supabase.rpc('update_post', {
      p_post_id: postId,
      p_subject: subject,
      p_content: content,
      p_tags: tags,
    });
    setEditSaving(false);

    if (error) { setEditError(editErrorMessage(error)); return; }

    const imagesChanged =
      JSON.stringify(editImages.map(i => i.path)) !== JSON.stringify(postImages.map(i => i.path));
    if (imagesChanged) {
      const { error: imgErr } = await syncContentImages({ postId, images: editImages });
      if (imgErr) { setEditError(imageErrorMessage(imgErr.message)); return; }
      setPostImages(editImages);
      sweepImageDeletions().catch(() => {});   // removed photos' files
    }

    setPost(prev => ({ ...prev, subject, content, tags, edited_at: new Date().toISOString() }));
    setEditingPost(false);
  }, [postId, editSaving, editSubject, editContent, editTags, editImages, postImages]);

  const startEditPost = useCallback(() => {
    setEditSubject(post?.subject ?? '');
    setEditContent(post?.content ?? '');
    setEditTags((post?.tags ?? []).join(', '));
    setEditImages(postImages);
    setEditError('');
    setEditingPost(true);
  }, [post, postImages]);

  // ── Submit reply ────────────────────────────────────────
  const handleDeletePost = useCallback(async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    const { error } = await supabase.rpc('delete_post', { p_post_id: postId });
    if (error) { showToast('Could not delete post.'); return; }
    sweepImageDeletions().catch(() => {});   // the post's photos are now orphans
    navigate(-1);
  }, [postId, navigate, showToast]);

  const handleDeleteReply = useCallback(async (replyId) => {
    if (!window.confirm('Delete this reply? This cannot be undone.')) return;
    const { error } = await supabase.rpc('delete_reply', { p_reply_id: replyId });
    if (error) { showToast('Could not delete reply.'); return; }
    sweepImageDeletions().catch(() => {});   // the reply's photos are now orphans
    setReplies(prev => prev.filter(r => r.id !== replyId));
    setPost(prev => (prev ? { ...prev, reply_count: Math.max((prev.reply_count ?? 1) - 1, 0) } : prev));
  }, [showToast]);

  const handleSubmitReply = useCallback(async () => {
    const content = replyContent.trim();
    if (replySubmitting || !content || !requireVerified()) return;

    setReplySubmitting(true);
    const { data: newReplyId, error } = await supabase.rpc('create_reply', {
      p_post_id: postId,
      p_content: content,
    });

    // Photos after the reply exists, never before.
    if (!error && newReplyId && replyImages.length) {
      const { error: imgErr } = await syncContentImages({ replyId: newReplyId, images: replyImages });
      if (imgErr) showToast(imageErrorMessage(imgErr.message));
    }
    setReplySubmitting(false);

    if (error) {
      const msg = error.message?.includes('not_verified')
        ? 'Verification required to reply.'
        : error.message?.includes('table_archived')
        ? 'This table is archived — no new replies.'
        : 'Could not post reply. Please try again.';
      showToast(msg);
      return;
    }

    setReplyContent('');
    setReplyImages([]);
    fetchReplies();
  }, [replySubmitting, replyContent, replyImages, postId, requireVerified, showToast, fetchReplies]);

  // ── Loading state ───────────────────────────────────────
  if (postLoading) {
    return (
      <div className="min-h-screen pb-nav">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="rounded-2xl p-5 animate-pulse" style={cardStyle}>
            <div className="flex gap-3 mb-4">
              <div className="w-10 h-10 rounded-full" style={{ backgroundColor: skeletonBg }} />
              <div className="flex-1 space-y-2">
                <div className="h-4 rounded w-1/3" style={{ backgroundColor: skeletonBg }} />
                <div className="h-3 rounded w-1/4" style={{ backgroundColor: skeletonBg }} />
              </div>
            </div>
            <div className="h-5 rounded w-2/3 mb-3" style={{ backgroundColor: skeletonBg }} />
            <div className="h-3 rounded w-full mb-2" style={{ backgroundColor: skeletonBg }} />
            <div className="h-3 rounded w-4/5 mb-2" style={{ backgroundColor: skeletonBg }} />
            <div className="h-3 rounded w-3/5" style={{ backgroundColor: skeletonBg }} />
          </div>
        </div>
      </div>
    );
  }

  // ── Error / not found ───────────────────────────────────
  if (postError || !post) {
    return (
      <div className="min-h-screen pb-nav">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(tableSlug ? `/community/${tableSlug}` : '/community')}
            className="flex items-center gap-1 mb-4 text-sm transition-colors hover:opacity-70"
            style={{ color: 'var(--text-secondary)' }}
          >
            <ChevronLeft size={16} />
            Back
          </button>
          <div className="text-center py-10">
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Post not found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-nav">
      {/* Mobile header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2">
          <button
            onClick={() => navigate(`/community/${tableSlug}`)}
            className="flex items-center gap-1 text-sm transition-colors text-white/70 hover:text-white"
          >
            <ChevronLeft size={18} />
            Back to Table
          </button>
        </div>
      </div>

      {/* Desktop back button */}
      <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5 pb-2">
        <button
          onClick={() => navigate(`/community/${tableSlug}`)}
          className="flex items-center gap-1 text-sm transition-colors hover:opacity-70"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ChevronLeft size={16} />
          Back to Table
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Post card */}
        <div className="rounded-2xl p-5 mb-6" style={cardStyle}>
          {/* Subject as headline */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <h1 className="font-bold text-base leading-snug" style={{ color: 'var(--text-primary)' }}>
              {post.subject}
            </h1>
            {currentUserId && post.author_id === currentUserId && !editingPost && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={startEditPost}
                  className="p-1.5 rounded-lg transition-all hover:scale-110 active:scale-95"
                  style={{ color: 'var(--text-tertiary)' }}
                  title="Edit post"
                >
                  <Pencil size={15} />
                </button>
                <button
                  onClick={handleDeletePost}
                  className="p-1.5 rounded-lg transition-all hover:scale-110 active:scale-95"
                  style={{ color: 'var(--text-tertiary)' }}
                  title="Delete post"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            )}
          </div>

          {/* Author row — two lines, matching the feed and table cards. */}
          <div className="flex items-start gap-2.5 mb-3">
            {post.author_avatar ? (
              <img
                src={post.author_avatar}
                alt=""
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br from-brand-primary to-brand-secondary">
                {(post.author_name || 'D')[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <button
                  onClick={() => navigate(`/profile/${post.author_id}`)}
                  className="block min-w-0 text-sm font-semibold text-primary truncate text-left hover:underline transition-colors"
                >
                  {post.author_name}
                </button>
                <TeamTag userId={post.author_id} />
              </div>
              <div className="flex items-center gap-1 text-xs min-w-0" style={{ color: 'var(--text-tertiary)' }}>
                {[getRoleLabel(post.author_role), post.author_agency].some(Boolean) && (
                  <span className="truncate">
                    {[getRoleLabel(post.author_role), post.author_agency].filter(Boolean).join(' · ')}
                  </span>
                )}
                <span className="flex-shrink-0">
                  {[getRoleLabel(post.author_role), post.author_agency].some(Boolean) ? '· ' : ''}{relativeTime(post.created_at)}
                </span>
                <EditedMark at={post.edited_at} />
              </div>
            </div>
          </div>

          {/* Content — or the editor, which replaces subject/body/tags together
              so the whole post is edited in one save rather than field by field. */}
          {editingPost ? (
            <div className="mb-3 space-y-2">
              <input
                type="text"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value.slice(0, 200))}
                placeholder="Subject"
                className="w-full px-3 py-2 rounded-lg text-sm font-medium text-primary focus:outline-none focus:ring-1 focus:ring-brand-secondary"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
              />
              <PostEditor
                value={editContent}
                onChange={setEditContent}
                maxLength={5000}
                ariaLabel="Edit post"
                onSubmit={handleSavePost}
                images={editImages}
                onImagesChange={setEditImages}
                userId={currentUserId}
              />
              <TagInput value={editTags} onChange={setEditTags} />
              {editError && (
                <p className="text-xs" style={{ color: 'var(--color-error)' }}>{editError}</p>
              )}
              <div className="flex items-center justify-end">
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingPost(false)}
                    className="px-3 py-1.5 rounded-lg text-xs transition-all active:scale-[0.98]"
                    style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePost}
                    disabled={editSaving || !editSubject.trim() || !editContent.trim()}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                  >
                    {editSaving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <FormattedText text={post.content} className="mb-3 leading-relaxed text-[15px] text-secondary" />
              <PostImages images={postImages} className="mb-3" />
              {firstYouTubeVideoId(post.content) && (
                <YouTubePreview videoId={firstYouTubeVideoId(post.content)} className="mb-3" />
              )}

              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {post.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-1 rounded-full text-xs"
                      style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-tertiary)' }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex items-center gap-5 text-tertiary pt-3" style={{ borderTop: '1px solid var(--card-border)' }}>
            {currentUserId && post.author_id === currentUserId ? (
              <span className="flex items-center gap-1.5" title="Helpful votes on your post">
                <ThumbsUp size={16} aria-hidden="true" />
                <span className="text-sm font-medium">{helpfulCount}<span className="sr-only"> helpful votes</span></span>
              </span>
            ) : (
              <button
                onClick={handleToggleLike}
                className="flex items-center gap-1.5 transition-all duration-200 hover:scale-110 active:scale-95"
                style={{ color: liked ? 'var(--brand-primary)' : undefined }}
                aria-label={liked ? 'Remove helpful' : 'Mark helpful'}
              >
                <ThumbsUp size={16} className={liked ? 'fill-current' : ''} />
                <span className="text-sm font-medium">{helpfulCount}</span>
              </button>
            )}
            <span className="flex items-center gap-1.5 text-sm">
              <span>{post.reply_count} replies</span>
            </span>
            {currentUserId !== post.author_id && (
              <button
                onClick={() => setReportTarget({ type: 'post', id: post.id })}
                className="ml-auto transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
                title="Report"
              >
                <Flag size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Replies */}
        <div>
          <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
            {replies.length === 1 ? '1 Reply' : `${replies.length} Replies`}
          </h2>

          {repliesLoading ? (
            <div className="rounded-2xl p-5 space-y-4" style={cardStyle}>
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: skeletonBg }} />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 rounded w-1/4" style={{ backgroundColor: skeletonBg }} />
                    <div className="h-3 rounded w-3/4" style={{ backgroundColor: skeletonBg }} />
                  </div>
                </div>
              ))}
            </div>
          ) : replies.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                No replies yet. Be the first to respond.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl px-5 py-1" style={cardStyle}>
              {replies.map((reply, i) => (
                <ReplyCard
                  key={reply.id}
                  reply={reply}
                  navigate={navigate}
                  isFirst={i === 0}
                  canReport={currentUserId !== reply.author_id}
                  onReport={() => setReportTarget({ type: 'reply', id: reply.id })}
                  canEdit={!!currentUserId && currentUserId === reply.author_id}
                  onSave={handleSaveReply}
                  onDelete={handleDeleteReply}
                  onToggleLike={handleToggleReplyLike}
                  userId={currentUserId}
                />
              ))}
            </div>
          )}
        </div>

        {/* Reply compose */}
        {tableArchived ? (
          <div
            className="rounded-2xl p-4 mt-4 text-center"
            style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
          >
            <p className="text-sm text-primary font-medium">This table is archived</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
              The conversation stays readable, but replies are closed.
            </p>
          </div>
        ) : (
        <div className="rounded-2xl p-4 mt-4" style={cardStyle}>
          <PostEditor
            value={replyContent}
            onChange={setReplyContent}
            placeholder="Write a reply..."
            ariaLabel="Reply"
            maxLength={2000}
            size="reply"
            onSubmit={handleSubmitReply}
            images={replyImages}
            onImagesChange={setReplyImages}
            userId={currentUserId}
          />
          <div className="flex justify-between items-center mt-3">
            <span className="text-xs text-tertiary hidden md:inline">⌘/Ctrl + Enter to send</span>
            <button
              onClick={handleSubmitReply}
              disabled={!replyContent.trim() || replySubmitting}
              className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              <Send size={16} />
              {replySubmitting ? 'Posting...' : 'Reply'}
            </button>
          </div>
        </div>
        )}
      </div>

      {showVerifyModal && (
        <VerifyModal onClose={() => setShowVerifyModal(false)} />
      )}
      {reportTarget && (
        <ReportModal
          contentType={reportTarget.type}
          contentId={reportTarget.id}
          onClose={() => setReportTarget(null)}
        />
      )}
      <Toast message={toastMsg} />
    </div>
  );
};

export default PostDetailScreen;
